/**
 * Typography utility functions for easier usage in styled components
 * 
 * Usage examples:
 * 
 * // In a styled component:
 * const StyledButton = styled.button`
 *   ${applyTypography('1_medium')}
 *   color: ${({ theme }) => theme.components.lyt_button.primary.text.default};
 * `;
 * 
 * // For custom combinations:
 * const CustomText = styled.p`
 *   ${customTypography(2, 'bold')}
 *   color: red;
 * `;
 * 
 * // For responsive typography:
 * const ResponsiveHeading = styled.h1`
 *   ${responsiveTypography({
 *     mobile: '1_bold',
 *     tablet: '2_bold', 
 *     desktop: '3_bold'
 *   })}
 * `;
 */

/**
 * Apply a predefined typography variant
 * @param {string} variant - Typography variant (e.g., '1_medium', '3_bold')
 * @returns {function} Styled-components template function
 */
export const applyTypography = (variant) => ({ theme }) => {
    // Defensive check for theme.typography existence
    if (!theme || !theme.typography || !theme.typography.variants) {
        console.warn(`Typography variant '${variant}' not found in theme. Falling back to basic styles.`);
        return `
            font-family: 'IBM Plex Sans';
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
        `;
    }
    
    const typographyFunction = theme.typography.variants[variant];
    if (!typographyFunction) {
        console.warn(`Typography variant '${variant}' not found. Available variants:`, Object.keys(theme.typography.variants));
        return '';
    }
    
    return typographyFunction(theme);
};

/**
 * Create custom typography by combining size and weight
 * @param {number|string} size - Size key (0, 1, 2, 3, 4)
 * @param {string} weight - Weight key (normal, medium, bold)
 * @param {string} lineHeight - Optional line height key (tight, normal, relaxed)
 * @returns {function} Styled-components template function
 */
const SIZE_TO_LINE_HEIGHT_KEY = {
    0: 'zeroPx',
    1: 'onePx',
    2: 'twoPx',
    3: 'threePx',
    4: 'fourPx'
};

export const customTypography = (size, weight, lineHeightOverride) => ({ theme }) => {
    const { fontFamily, sizes, weights, lineHeight: lineHeights } = theme.typography;
    const resolvedSize = sizes[size] ? size : 2;
    const lineHeightKey = lineHeightOverride || SIZE_TO_LINE_HEIGHT_KEY[resolvedSize] || 'twoPx';
    
    return `
        font-family: ${fontFamily.primary};
        font-size: ${sizes[resolvedSize]};
        font-weight: ${weights[weight] || weights.normal};
        line-height: ${lineHeights[lineHeightKey] || lineHeights.twoPx};
    `;
};

/**
 * Apply responsive typography across breakpoints
 * @param {object} variants - Object with mobile, tablet, desktop variant keys
 * @returns {function} Styled-components template function
 */
export const responsiveTypography = (variants) => ({ theme }) => {
    let css = '';
    
    // Mobile first (default)
    if (variants.mobile) {
        const mobileVariant = theme.typography.variants[variants.mobile];
        css += mobileVariant ? mobileVariant(theme) : '';
    }
    
    // Tablet breakpoint
    if (variants.tablet) {
        const tabletVariant = theme.typography.variants[variants.tablet];
        if (tabletVariant) {
            css += `
                @media (min-width: 768px) {
                    ${tabletVariant(theme)}
                }
            `;
        }
    }
    
    // Desktop breakpoint
    if (variants.desktop) {
        const desktopVariant = theme.typography.variants[variants.desktop];
        if (desktopVariant) {
            css += `
                @media (min-width: 1024px) {
                    ${desktopVariant(theme)}
                }
            `;
        }
    }
    
    return css;
};

/**
 * Get typography tokens for use in calculations or conditional logic
 * @param {object} theme - Theme object
 * @returns {object} Typography tokens
 */
export const getTypographyTokens = (theme) => theme.typography;

/**
 * Available typography variants for reference
 */
export const TYPOGRAPHY_VARIANTS = {
    // 12px variants (size 0)
    SIZE_0_NORMAL: '0_normal',
    SIZE_0_MEDIUM: '0_medium', 
    SIZE_0_BOLD: '0_bold',
    
    // 14px variants (size 1)
    SIZE_1_NORMAL: '1_normal',
    SIZE_1_MEDIUM: '1_medium',
    SIZE_1_BOLD: '1_bold',
    
    // 16px variants (size 2)
    SIZE_2_NORMAL: '2_normal',
    SIZE_2_MEDIUM: '2_medium',
    SIZE_2_BOLD: '2_bold',
    
    // 24px variants (size 3)
    SIZE_3_NORMAL: '3_normal',
    SIZE_3_MEDIUM: '3_medium',
    SIZE_3_BOLD: '3_bold',
    
    // 28px variants (size 4)
    SIZE_4_NORMAL: '4_normal',
    SIZE_4_MEDIUM: '4_medium',
    SIZE_4_BOLD: '4_bold'
}; 